import { Types } from 'mongoose';

export type ResultConfig = {
  type: 'globalScore' | 'pie1D' | 'scoreCard';
  codes: string[];
};

export type Co2PdfPageConfig = {
  totalIndicatorCode: string;
  descriptionTextCode: string;
  detailedResultCode: string;
  detailedUnit: string;
};

export type SocialUtilityPdfPageConfig = {
  resultCode: string;
  descriptionTextCode: string;
  detailedResult1Code: string;
  detailedResult2Code: string;
};

export type PdfResultsConfig = {
  co2Page: Co2PdfPageConfig;
  socialUtilityPage: SocialUtilityPdfPageConfig;
};

export type ConfigurationMap = {
  'results.config': ResultConfig[];
  'pdfResults.config': PdfResultsConfig;
  [key: string]: unknown;
};

export interface Report {
  _id: Types.ObjectId;
  name: string;
  users: ProjectUser[];
  results: Result[];
  projects: Project[];
  createdAt: Date;
  updatedAt: Date;
}

export type ValueAndAggregationMethod = {
  value: number;
  aggregationMethod: AggregationMethod;
};

export enum ModelStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  ARCHIVED = 'archived',
}

export interface Model {
  _id: Types.ObjectId;
  name: string;
  singularName: string;
  type: string;
  color: string;
  hasValidation: boolean;
  hasRequiredFields: boolean;
  description: string;
  spreadsheetId: string;
  parameters: Parameter[];
  config: Configuration;
  changelog: string;
  userInformation: string;
  status: ModelStatus;
  publishedAt: Date;
  versionNumber: number;
  defaultLanguage: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface Reference {
  index: number;
  value: Types.ObjectId[];
}

export interface ProrataInformation {
  projectSourceName: string;
  prorata: number;
}
export type ProjectModifications = Partial<
  Pick<
    Project,
    | 'sectors'
    | 'completionRate'
    | 'uncompleted'
    | 'results'
    | 'name'
    | 'year'
    | 'finalizedAt'
    | 'tour'
    | 'spreadsheetId'
    | 'users'
  >
>;

export interface Address {
  street?: string;
  postalCode?: string;
  city?: string;
  region?: string;
  country?: string;
}

export interface Project {
  _id: Types.ObjectId;
  userId: Types.ObjectId;
  users: ProjectUser[];
  name: string;
  year: number;
  address?: Address;
  sectors: Sector[];
  completionRate: number;
  uncompleted: Partial<Parameter>[];
  new: Partial<Parameter>[];
  spreadsheetId: string;
  results: Result[];
  actions: Action[];
  createdAt: Date;
  updatedAt: Date;
  tour: any;
  model: Model;
  references: Reference[];
  public: boolean;
  finalizedAt?: Date;
  company: string;
  prorataInformation?: ProrataInformation;
}

export interface ProjectUser {
  id: Types.ObjectId;
  role: string;
  user: any;
  lastSeenAt?: Date;
}

export interface Sector {
  information?: any;
  sectors: Sector[];
  parameters: Parameter[];
  completionRate?: number;
}

export type Value = Date | number | string | string[] | undefined;

export interface Parameter {
  id?: string;
  index: number;
  name?: string;
  unit?: string;
  // description?: string;
  type?: string;
  initialValue: Value;
  value: Value;
  exportedValue: Value;
  possibleValues?: string[];
  min?: number;
  max?: number;
  step?: number;
  display?: boolean;
  required?: boolean;
  configError?: string;
  valueError?: number;
  displayOnCreate?: boolean;
  // information?: string;
}

export interface AbacusColor {
  primary: string;
  secondary: string;
}

export interface Abacus {
  letters: string[];
  colors: AbacusColor[];
  scores: string[];
}

export interface ScoreCard {
  type: 'scoreCard';
  scope?: string;
  display?: boolean;
  code?: string;
  title?: string;
  level?: string;
  score?: string;
  abacus?: Abacus;
  filter?: string;
}

export interface Indicator {
  type: 'indicator';
  scope?: string;
  code?: string;
  title?: string;
  number?: string;
  unit?: string;
  filter?: string;
  display?: boolean;
}

export interface Indicator2Values {
  type: 'indicator2Values';
  scope?: string;
  code?: string;
  title?: string;
  displayed_number1?: string;
  displayed_unit1?: string;
  number1?: string;
  unit1?: string;
  displayed_number2?: string;
  displayed_unit2?: string;
  number2?: string;
  unit2?: string;
  filter?: string;
  display?: boolean;
}

export interface TreemapNode {
  name: string;
  color: string;
  children?: TreemapNode[];
  loc?: number;
  unit?: string;
}

export interface TreemapData {
  name: string;
  color: string;
  children: TreemapNode[];
}

export interface Treemap {
  type: 'treemap';
  scope?: string;
  code?: string;
  title?: string;
  size?: string;
  unit?: string;
  subtitle?: string;
  description?: string;
  data?: TreemapData;
  filter?: string;
  display?: boolean;
}

export interface Bar1DBar {
  name: string;
  color: string;
}

export interface Bar1DData {
  [key: string]: number | string;
}

export interface Bar1D {
  type: 'bar1D';
  scope?: string;
  code?: string;
  title?: string;
  size?: string;
  unit?: string;
  subtitle?: string;
  description?: string;
  data?: Bar1DData[];
  bars?: Bar1DBar[];
  filter?: string;
  display?: boolean;
}

export interface Pie1DData {
  name: string;
  fill: string;
  value: number;
  link?: string;
}

export interface Pie1D {
  type: 'pie1D';
  scope?: string;
  code?: string;
  title?: string;
  unit?: string;
  size?: string;
  subtitle?: string;
  description?: string;
  data?: Pie1DData[];
  filter?: string;
  display?: boolean;
}

export interface BarData {
  [key: string]: string | number | any;
}

export interface BarStacked2DData {
  categoryData: BarData[];
  barData: BarData[];
}

export interface BarStacked2D {
  type: 'barStacked2D';
  scope?: string;
  code?: string;
  title?: string;
  size?: string;
  unit?: string;
  subtitle?: string;
  description?: string;
  data?: BarStacked2DData;
  filter?: string;
  display?: boolean;
}

export interface BarStacked1DData {
  barData: BarData[];
  data: BarData[];
}

export interface BarStacked1D {
  type: 'barStacked1D';
  scope?: string;
  code?: string;
  title?: string;
  size?: string;
  unit?: string;
  subtitle?: string;
  description?: string;
  data?: BarStacked1DData;
  filter?: string;
  display?: boolean;
}

export interface BarSingle1DData {
  barData: BarData[];
  data: BarData[];
}

export interface BarSingle1D {
  type: 'barSingle1D';
  scope?: string;
  code?: string;
  title?: string;
  size?: string;
  unit?: string;
  subtitle?: string;
  description?: string;
  data?: BarSingle1DData;
  filter?: string;
  display?: boolean;
}

export interface NavElement {
  icon: string;
  link: string;
  title: string;
}

export interface Nav {
  type: 'nav';
  scope: string;
  code?: string;
  data: NavElement[];
  filter: string;
  display?: boolean;
}

export interface Toggle {
  type: 'toggle';
  code?: string;
  scope: string;
  default: string;
  selected: string;
  values: string[];
  display?: boolean;
}

export interface Title {
  type: 'title';
  scope?: string;
  icon?: string;
  text?: string;
  code?: string;
  filter?: string;
  display?: boolean;
}

export interface Subtitle {
  type: 'subtitle';
  scope?: string;
  text?: string;
  code?: string;
  filter?: string;
  display?: boolean;
}

export interface GlobalScore {
  type: 'globalScore';
  scope?: string;
  title?: string;
  subtitle?: string;
  score?: string;
  text?: string;
  text_en?: string;
  text_fr?: string;
  unit?: string;
  code?: string;
  uncertainty?: number;
  filter?: string;
  display?: boolean;
}

export interface Impact {
  scope: string;
  value: number | null;
  unit: string;
  percentage: number | null;
  absolutePercentage: number | null;
  maxScore: number | null;
  currentScore: number | null;
}

export interface Action {
  sector: string;
  types: string[];
  title: string;
  description: string;
  impacts: Impact[];
  cost: number;
  humanResources: number;
  duration: number;
  impactValue: number;
  display?: boolean;
}

export interface TextResult {
  type: 'text';
  scope?: string;
  text?: string;
  code?: string;
  filter?: string;
  display?: boolean;
}
export interface TableResult {
  type: 'table';
  title?: string;
  code?: string;
  scope?: string;
  display?: boolean;
  description?: string;
  table?: any[][];
  filter?: string;
}

export enum AggregationMethod {
  SUM = 'SUM',
  AVERAGE = 'AVG',
}

export type Result =
  | ScoreCard
  | Indicator
  | Indicator2Values
  | Treemap
  | Nav
  | Toggle
  | Title
  | Subtitle
  | Pie1D
  | BarStacked2D
  | BarStacked1D
  | BarSingle1D
  | Bar1D
  | GlobalScore
  | TextResult
  | TableResult

export interface Statistic {
  type: string;
  name: string;
  projects: Project[];
  data: any;
  parametersConfig: ParametersConfiguration;
}

export interface Statistics {
  [index: number]: Statistic;
}

export interface SectorConfiguration {
  color: string;
  icon?: string;
  children?: SectorConfiguration[];
}

export interface ParametersConfiguration {
  range: string;
  columnIndexes: {
    sectors: number[];
    parameters: {
      type: number;
      value: number;
      [key: string]: number;
    };
  };
  sectors: SectorConfiguration[];
  defaultTitle: string;
  titleParameterId: string[];
  yearParameterId: string;
  addressParameterId: string;
  types: { [key: string]: string };
  externalModules?: {
    [key: string]: string[];
  };
}

export interface Operations {
  [key: string]:
  | undefined
  | ((row: any[], value: any, config: ParametersConfiguration) => any);
}

export interface ResultsConfiguration {
  range: string;
}

export interface ActionsConfiguration {
  range: string;
  columnIndexes: {
    sector: number;
    display: number;
    types: number;
    title: number;
    scope: number;
    value: number;
    unit: number;
    percentage: number;
    cost: number;
    humanResources: number;
    maxScore: number;
    currentScore: number;
    duration: number;
    description: number;
    absolutePercentage: number;
  };
}

export interface Configuration {
  parameters: ParametersConfiguration;
  results: ResultsConfiguration;
  actions: ActionsConfiguration;
}

export interface IndexParameterInput {
  type: 'index';
  index: number;
  value: Value;
}

export interface IdParameterInput {
  type: 'id';
  id: string;
  value: Value;
}

export type ParameterInput = IndexParameterInput | IdParameterInput;
